import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { User, Lock, ChevronLeft, Eye, EyeOff, Check, X, Phone, Square, CheckSquare, ShieldAlert } from 'lucide-react';
import { Button } from './ui/button';
import { GoogleSignInButton } from './GoogleSignInButton';
import { GoogleUsernameModal } from './GoogleUsernameModal';
import { Input } from './ui/input';
import afroYapLogo from '../assets/afroyap_full_logo.png';
const afroYapLogoBlue = afroYapLogo;
import { ParallaxBackground } from './utils/parallax';
import { AuthService } from '../services/api';
import { toast } from 'sonner';
import { useTheme } from './ThemeProvider';

// ── Legal constants ───────────────────────────────────────────
const POLICY_VERSION = '1.0';
const MIN_AGE = 16;

interface SignupScreenProps {
  onBack: () => void;
  onSignupComplete: () => void;
}

export function SignupScreen({ onBack, onSignupComplete }: SignupScreenProps) {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleProfile, setGoogleProfile] = useState<any>(null);
  const [error, setError] = useState('');
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  // ── Age gate & policy agreement ───────────────────────────────
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const { theme } = useTheme();

  const passwordRequirements = {
    length: formData.password.length >= 8,
    uppercase: /[A-Z]/.test(formData.password),
    lowercase: /[a-z]/.test(formData.password),
    number: /[0-9]/.test(formData.password)
  };

  const allRequirementsMet = passwordRequirements.length && passwordRequirements.uppercase && passwordRequirements.lowercase && passwordRequirements.number;

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (formData.username.length >= 3) {
      setUsernameStatus('checking');
      timeout = setTimeout(async () => {
        try {
          const available = await AuthService.checkUsernameAvailability(formData.username);
          setUsernameStatus(available ? 'available' : 'taken');
        } catch (e) {
          setUsernameStatus('idle');
        }
      }, 500);
    } else {
      setUsernameStatus('idle');
    }
    return () => clearTimeout(timeout);
  }, [formData.username]);



  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    // 🔒 AGE GATE: Must confirm age 16+
    if (!ageConfirmed) {
      setError('You must confirm that you are at least 16 years old to create an account.');
      toast.error('Age confirmation required');
      return;
    }

    // 🔒 POLICY GATE: Must agree to Terms & Privacy Policy
    if (!policyAccepted) {
      setError('You must agree to the Terms of Service and Privacy Policy to create an account.');
      toast.error('Policy agreement required');
      return;
    }

    // 🔒 PATCH: Normalize username to lowercase for consistency
    const normalizedUsername = formData.username.replace('@', '').toLowerCase();

    // 🔒 PATCH: Validate username format strict
    const usernameRegex = /^[a-zA-Z0-9_.]+$/;
    if (!usernameRegex.test(formData.username)) {
      setError('Invalid username format. Use only letters, numbers, underscores, and periods.');
      toast.error('Invalid username format');
      setUsernameStatus('taken'); // Visual feedback
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      toast.error('Passwords do not match');
      return;
    }
    if (!allRequirementsMet) {
      setError('Password does not meet requirements');
      toast.error('Password does not meet requirements');
      return;
    }
    setError('');
    setLoading(true);

    const signupTimeout = setTimeout(() => {
      setLoading(false);
      setError('Signup is taking longer than expected. Please try again.');
      toast.error('Signup timed out. Please try again.');
    }, 50000);

    try {
      // 🚀 LAUNCHED: User cap removed. AfroYap is open to all users.
      const policyAcceptedAt = new Date().toISOString();

      await AuthService.signUpWithUsername(normalizedUsername, formData.password, {
        policyAcceptedAt,
        policyVersion: POLICY_VERSION,
        ageConfirmed: true,
        ageConfirmedAt: policyAcceptedAt,
        minAgeAtSignup: MIN_AGE,
      });
      clearTimeout(signupTimeout);
      onSignupComplete();
    } catch (err: any) {
      clearTimeout(signupTimeout);
      setError(err.message || 'Signup failed');
      toast.error(err.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] flex items-center justify-center p-6 relative overflow-y-auto">
      <div className="absolute inset-0 pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>

      <motion.div
        className="relative z-10 w-full max-w-[340px] flex flex-col py-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <button onClick={onBack} className="flex items-center text-zinc-700 dark:text-zinc-300 hover:text-orange-500 dark:hover:text-blue-500 transition-colors text-xs font-bold mb-6">
          <ChevronLeft className="w-4 h-4 mr-1" /> BACK
        </button>



        {/* FORM */}
        <form onSubmit={handleSignup} className="space-y-4">
          <div className="relative w-full max-w-[280px] mx-auto">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
              <User className="w-5 h-5 text-zinc-500" />
            </div>
            <Input
              type="text"
              placeholder="Username"
              value={formData.username}
              onChange={(e) => {
                const val = e.target.value;
                // Check if user pressed space
                if (val.includes(' ')) {
                  toast.error('Spaces are not allowed in usernames');
                  setUsernameStatus('taken');
                }
                // Only allow valid characters
                const sanitized = val.replace(/[^a-zA-Z0-9_.]/g, '');
                setFormData({ ...formData, username: sanitized });
              }}
              className="w-full h-12 pl-12 pr-12 bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-xl text-sm placeholder:text-zinc-400 focus:border-[#F39C12] dark:focus:border-[#3B82F6] transition-all"
              style={{
                backgroundImage: theme === 'dark'
                  ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 100%)'
                  : 'linear-gradient(135deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0) 100%)'
              }}
            />
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              {usernameStatus === 'checking' && <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />}
              {usernameStatus === 'available' && <Check className="w-5 h-5 text-green-500" />}
              {usernameStatus === 'taken' && <X className="w-5 h-5 text-zinc-500" />}
            </div>
          </div>

          <div className="relative w-full max-w-[280px] mx-auto">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
              <Lock className="w-5 h-5 text-zinc-500" />
            </div>
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              disabled={loading}
              className="w-full h-12 pl-12 pr-12 bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-xl text-sm placeholder:text-zinc-400 focus:border-[#F39C12] dark:focus:border-[#3B82F6] transition-all"
              style={{
                backgroundImage: theme === 'dark'
                  ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 100%)'
                  : 'linear-gradient(135deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0) 100%)'
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-700"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          {/* PASSWORD REQUIREMENTS */}
          <div className="w-full max-w-[280px] mx-auto flex justify-center gap-2 px-2 text-[8px] font-bold">
            <span className={`flex items-center gap-1 ${passwordRequirements.length ? 'text-green-600 dark:text-green-400' : 'text-zinc-400'}`}>
              {passwordRequirements.length && <Check className="w-3 h-3" />}
              8+ Chars
            </span>
            <span className={`flex items-center gap-1 ${passwordRequirements.uppercase ? 'text-green-600 dark:text-green-400' : 'text-zinc-400'}`}>
              {passwordRequirements.uppercase && <Check className="w-3 h-3" />}
              ABC
            </span>
            <span className={`flex items-center gap-1 ${passwordRequirements.lowercase ? 'text-green-600 dark:text-green-400' : 'text-zinc-400'}`}>
              {passwordRequirements.lowercase && <Check className="w-3 h-3" />}
              abc
            </span>
            <span className={`flex items-center gap-1 ${passwordRequirements.number ? 'text-green-600 dark:text-green-400' : 'text-zinc-400'}`}>
              {passwordRequirements.number && <Check className="w-3 h-3" />}
              123
            </span>
          </div>

          <div className="relative w-full max-w-[280px] mx-auto">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
              <Lock className="w-5 h-5 text-zinc-500" />
            </div>
            <Input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm Password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              required
              disabled={loading}
              className="w-full h-12 pl-12 pr-12 bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-xl text-sm placeholder:text-zinc-400 focus:border-[#F39C12] dark:focus:border-[#3B82F6] transition-all"
              style={{
                backgroundImage: theme === 'dark'
                  ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 100%)'
                  : 'linear-gradient(135deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0) 100%)'
              }}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-700"
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>


          {/* AGE GATE & POLICY CHECKBOXES */}
          <div className="w-full max-w-[280px] mx-auto space-y-3 pt-2">
            {/* Age confirmation */}
            <button
              type="button"
              onClick={() => setAgeConfirmed(!ageConfirmed)}
              className="flex items-start gap-3 w-full text-left group"
            >
              <div className={`mt-0.5 shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                ageConfirmed
                  ? 'bg-orange-500 dark:bg-blue-500 border-orange-500 dark:border-blue-500'
                  : 'border-zinc-400 dark:border-zinc-600'
              }`}>
                {ageConfirmed && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
              </div>
              <span className="text-[11px] text-zinc-700 dark:text-zinc-300 leading-relaxed">
                I confirm that I am at least <strong>16 years old</strong>.
              </span>
            </button>

            {/* Terms & Privacy Policy acceptance */}
            <button
              type="button"
              onClick={() => setPolicyAccepted(!policyAccepted)}
              className="flex items-start gap-3 w-full text-left group"
            >
              <div className={`mt-0.5 shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                policyAccepted
                  ? 'bg-orange-500 dark:bg-blue-500 border-orange-500 dark:border-blue-500'
                  : 'border-zinc-400 dark:border-zinc-600'
              }`}>
                {policyAccepted && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
              </div>
              <span className="text-[11px] text-zinc-700 dark:text-zinc-300 leading-relaxed">
                I agree to the{' '}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: { screen: 'legal', type: 'terms' } })); }}
                  className="underline font-bold text-orange-600 dark:text-blue-400"
                >Terms of Service</button>
                {' '}and{' '}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: { screen: 'legal', type: 'privacy' } })); }}
                  className="underline font-bold text-orange-600 dark:text-blue-400"
                >Privacy Policy</button>.
              </span>
            </button>
          </div>

          <div className="flex justify-center w-full">
            <Button
              type="submit"
              disabled={loading || usernameStatus !== 'available' || formData.password !== formData.confirmPassword || !allRequirementsMet || !ageConfirmed || !policyAccepted}
              className="w-full max-w-[280px] h-12 rounded-xl font-bold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-base border-none text-white"
              style={{
                backgroundImage: theme === 'dark'
                  ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)'
                  : 'linear-gradient(135deg, #F39C12 0%, #E74C3C 100%)'
              }}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </div>
        </form>

        {/* OR DIVIDER + GOOGLE */}
        <div className="w-full max-w-[280px] mx-auto flex items-center gap-4 my-6">
          <div className="flex-1 h-px bg-black/10 dark:bg-white/10" />
          <span className="text-sm font-bold text-zinc-800 dark:text-zinc-200">or</span>
          <div className="flex-1 h-px bg-black/10 dark:bg-white/10" />
        </div>
        <div className="flex justify-center w-full">
          <GoogleSignInButton
            disabled={loading}
            onNeedsUsername={(prof) => setGoogleProfile(prof)}
            onSignedIn={() => onSignupComplete()}
          />
        </div>

        <GoogleUsernameModal
          open={!!googleProfile}
          profile={googleProfile}
          onComplete={() => { setGoogleProfile(null); onSignupComplete(); }}
          onCancel={async () => {
            setGoogleProfile(null);
            try { await AuthService.logout(); } catch { /* noop */ }
          }}
        />

        <p className="mt-6 text-center text-sm font-black text-zinc-800 dark:text-white">
          Create your account and start yapping!
        </p>

        {error && (
          <p className="mt-4 text-center text-xs font-bold text-red-500 bg-red-50 dark:bg-red-900/20 p-3 rounded-xl">
            {error}
          </p>
        )}
      </motion.div>
    </div>
  );
}
